import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="container mx-auto p-6 md:p-10 max-w-4xl text-gray-800">
      <h1 className="text-3xl font-bold mb-2">Política de Privacidade — JurisKey</h1>
      <p className="text-sm text-gray-500 mb-8">Última Atualização: 23 de março de 2026</p>

      <p className="mb-4">
        Esta Política de Privacidade descreve de forma detalhada como a extensão <strong>JurisKey</strong> ("Extensão", "Nós")
        coleta, processa, armazena e compartilha dados pessoais e sensíveis dos usuários. Ela se aplica à extensão
        para navegadores Chrome e ao serviço de backend associado (API JurisKey).
      </p>

      {/* ======================================================= */}
      <h2 className="text-xl font-semibold mt-8 mb-3">1. Propósito da Extensão</h2>
      <p className="mb-4">
        O JurisKey é uma extensão de gerenciamento de autenticação multifator (TOTP) e login automático
        voltada exclusivamente para advogados e operadores jurídicos que utilizam sistemas judiciais eletrônicos
        brasileiros (PJe, EPROC, eSAJ, PROJUDI e similares). Suas funcionalidades principais são:
      </p>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
        <li>Gerar e gerenciar códigos TOTP (autenticação de dois fatores) para sistemas judiciais.</li>
        <li>Realizar login automático em portais judiciais usando certificado digital ou credenciais armazenadas de forma segura.</li>
        <li>Adicionar segredos TOTP via digitação manual, escaneamento de tela ou câmera (webcam).</li>
      </ul>

      {/* ======================================================= */}
      <h2 className="text-xl font-semibold mt-8 mb-3">2. Dados Coletados, Finalidade e Armazenamento</h2>
      <p className="mb-4">
        A tabela a seguir detalha todos os dados coletados pela Extensão e pelo Backend associado:
      </p>

      <div className="overflow-x-auto my-4">
        <table className="min-w-full bg-white border border-gray-200 text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="py-2 px-4 border-b text-left">Categoria</th>
              <th className="py-2 px-4 border-b text-left">Dados Específicos</th>
              <th className="py-2 px-4 border-b text-left">Finalidade</th>
              <th className="py-2 px-4 border-b text-left">Armazenamento</th>
              <th className="py-2 px-4 border-b text-left">Retenção</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="py-2 px-4 border-b align-top">Identificação do usuário</td>
              <td className="py-2 px-4 border-b align-top">Endereço de e-mail</td>
              <td className="py-2 px-4 border-b align-top">Autenticação e identificação do usuário na plataforma multi-tenant.</td>
              <td className="py-2 px-4 border-b align-top">Backend (PostgreSQL — Render)</td>
              <td className="py-2 px-4 border-b align-top">Enquanto a conta estiver ativa.</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b align-top">Credenciais de acesso</td>
              <td className="py-2 px-4 border-b align-top">Senha de acesso à plataforma JurisKey (hash bcrypt)</td>
              <td className="py-2 px-4 border-b align-top">Autenticação segura na plataforma.</td>
              <td className="py-2 px-4 border-b align-top">Backend (PostgreSQL)</td>
              <td className="py-2 px-4 border-b align-top">Enquanto a conta estiver ativa.</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b align-top">Token de sessão</td>
              <td className="py-2 px-4 border-b align-top">Token JWT gerado após o login bem-sucedido</td>
              <td className="py-2 px-4 border-b align-top">Autenticar requisições à API. Expirado automaticamente após inatividade de 15 minutos.</td>
              <td className="py-2 px-4 border-b align-top">Dispositivo local (chrome.storage.local)</td>
              <td className="py-2 px-4 border-b align-top">Removido ao logout ou após 15 min de inatividade.</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b align-top">Segredos TOTP</td>
              <td className="py-2 px-4 border-b align-top">Chave secreta TOTP (ex: obtida via QR code ou digitação manual)</td>
              <td className="py-2 px-4 border-b align-top">Gerar códigos OTP de autenticação de dois fatores para sistemas judiciais.</td>
              <td className="py-2 px-4 border-b align-top">Backend (PostgreSQL) — criptografado com Fernet + KEK por organização. Também cacheado localmente em chrome.storage.local (criptografado).</td>
              <td className="py-2 px-4 border-b align-top">Até o usuário excluir o segredo.</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b align-top">Certificados digitais</td>
              <td className="py-2 px-4 border-b align-top">Arquivo PFX/P12 (certificado digital), CPF e senha do certificado</td>
              <td className="py-2 px-4 border-b align-top">Realizar assinatura digital e login automático em sistemas judiciais que exigem certificado digital (PJe, eSAJ).</td>
              <td className="py-2 px-4 border-b align-top">Backend (PostgreSQL) — CPF e senha criptografados com Fernet. Conteúdo do certificado criptografado em repouso.</td>
              <td className="py-2 px-4 border-b align-top">Até o usuário excluir o certificado.</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b align-top">Credenciais de tribunais</td>
              <td className="py-2 px-4 border-b align-top">Nome de usuário (CPF ou login) e senha dos portais judiciais (EPROC, PROJUDI, etc.)</td>
              <td className="py-2 px-4 border-b align-top">Preencher automaticamente os campos de login nos portais judiciais durante o processo de auto-login.</td>
              <td className="py-2 px-4 border-b align-top">Backend (PostgreSQL) — criptografados com Fernet por organização.</td>
              <td className="py-2 px-4 border-b align-top">Até o usuário excluir a credencial.</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b align-top">Endereço IP</td>
              <td className="py-2 px-4 border-b align-top">IP de origem das requisições</td>
              <td className="py-2 px-4 border-b align-top">Segurança: auditoria de acesso, detecção de uso suspeito e limitação de taxa (rate limiting).</td>
              <td className="py-2 px-4 border-b align-top">Backend (logs de auditoria — PostgreSQL)</td>
              <td className="py-2 px-4 border-b align-top">Até 90 dias nos logs de auditoria.</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b align-top">URL da aba ativa</td>
              <td className="py-2 px-4 border-b align-top">URL da página aberta no navegador no momento da ação de auto-login</td>
              <td className="py-2 px-4 border-b align-top">Identificar o portal judicial e aplicar os seletores CSS corretos para preenchimento dos campos de login. Não é transmitida ao backend.</td>
              <td className="py-2 px-4 border-b align-top">Processada localmente na extensão. Não armazenada.</td>
              <td className="py-2 px-4 border-b align-top">Não retida.</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b align-top">Cookies de domínios judiciais</td>
              <td className="py-2 px-4 border-b align-top">Cookies de sessão dos portais *.jus.br</td>
              <td className="py-2 px-4 border-b align-top">Limpeza de sessões antigas antes de iniciar o auto-login para evitar conflitos de autenticação. Os cookies não são lidos nem transmitidos — apenas removidos.</td>
              <td className="py-2 px-4 border-b align-top">Não lidos nem armazenados. Apenas removidos do navegador.</td>
              <td className="py-2 px-4 border-b align-top">Não retidos.</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b align-top">Cache HTTP de domínios judiciais</td>
              <td className="py-2 px-4 border-b align-top">Cache de recursos HTTP dos portais *.jus.br</td>
              <td className="py-2 px-4 border-b align-top">Limpeza de cache obsoleto antes do auto-login para garantir que a sessão inicie limpa.</td>
              <td className="py-2 px-4 border-b align-top">Não lido nem armazenado. Apenas removido do navegador.</td>
              <td className="py-2 px-4 border-b align-top">Não retido.</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b align-top">Imagem da câmera (webcam)</td>
              <td className="py-2 px-4 border-b align-top">Frames de vídeo capturados pela webcam</td>
              <td className="py-2 px-4 border-b align-top">Escanear QR codes de segredos TOTP. O processamento é 100% local (jsQR). Nenhum frame é transmitido ao backend.</td>
              <td className="py-2 px-4 border-b align-top">Processado localmente em memória. Não armazenado nem transmitido.</td>
              <td className="py-2 px-4 border-b align-top">Não retido.</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b align-top">Captura de tela</td>
              <td className="py-2 px-4 border-b align-top">Screenshot da área da tela selecionada pelo usuário</td>
              <td className="py-2 px-4 border-b align-top">Escanear QR codes de segredos TOTP exibidos em tela. Processado localmente. Não transmitido.</td>
              <td className="py-2 px-4 border-b align-top">Processado localmente em memória. Não armazenado nem transmitido.</td>
              <td className="py-2 px-4 border-b align-top">Não retido.</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b align-top">Logs de auditoria</td>
              <td className="py-2 px-4 border-b align-top">Ações realizadas pelo usuário (login, geração de TOTP, upload de certificado, etc.) com timestamp e IP</td>
              <td className="py-2 px-4 border-b align-top">Segurança: rastreabilidade de ações para fins de auditoria e detecção de uso indevido. Apenas administradores da organização e super-administradores têm acesso.</td>
              <td className="py-2 px-4 border-b align-top">Backend (PostgreSQL)</td>
              <td className="py-2 px-4 border-b align-top">Até 90 dias.</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ======================================================= */}
      <h2 className="text-xl font-semibold mt-8 mb-3">3. Segurança e Criptografia</h2>
      <ul className="list-disc list-inside ml-4 space-y-2 mb-4">
        <li>
          <strong>Criptografia em Trânsito:</strong> Toda comunicação entre a Extensão e o Backend ocorre exclusivamente via HTTPS/TLS.
        </li>
        <li>
          <strong>Criptografia em Repouso — Envelope Encryption:</strong> Segredos TOTP, certificados digitais e credenciais de tribunais são criptografados com o algoritmo Fernet (AES-128-CBC + HMAC-SHA256). Cada organização possui uma chave de criptografia de chave (KEK) distinta, garantindo isolamento entre organizações.
        </li>
        <li>
          <strong>Senhas de usuário:</strong> Armazenadas exclusivamente como hash bcrypt — nunca em texto plano.
        </li>
        <li>
          <strong>Geração local do código TOTP:</strong> O código OTP final é calculado localmente no dispositivo do usuário usando a biblioteca otpauth.js, após a recuperação segura do segredo via API autenticada.
        </li>
        <li>
          <strong>Integridade de auditoria:</strong> Os registros de auditoria possuem hash SHA-256 para garantir integridade contra adulteração.
        </li>
        <li>
          <strong>Proteção contra gerenciador de senhas:</strong> A Extensão desabilita o gerenciador de senhas nativo do Chrome (via Chrome Privacy API) para os domínios judiciais durante o auto-login, evitando que credenciais sensíveis sejam salvas involuntariamente pelo navegador.
        </li>
        <li>
          <strong>Timeout de inatividade:</strong> O token de sessão é removido automaticamente após 15 minutos de inatividade do usuário no popup da extensão.
        </li>
      </ul>

      {/* ======================================================= */}
      <h2 className="text-xl font-semibold mt-8 mb-3">4. Compartilhamento de Dados com Terceiros</h2>
      <p className="mb-4">
        Nós <strong>não vendemos, alugamos nem transferimos</strong> dados pessoais dos usuários a terceiros para fins
        publicitários, de marketing ou qualquer finalidade não relacionada ao funcionamento do JurisKey.
      </p>
      <p className="mb-2">Os dados são compartilhados somente nos seguintes cenários técnicos necessários:</p>
      <ul className="list-disc list-inside ml-4 space-y-2 mb-4">
        <li>
          <strong>Backend JurisKey (Render.com):</strong> Servidor de aplicação responsável pela autenticação e armazenamento seguro dos dados. O provedor de hospedagem (Render.com) não tem acesso ao conteúdo criptografado dos dados dos usuários.
        </li>
        <li>
          <strong>Portais judiciais (*.jus.br):</strong> Durante o auto-login, as credenciais descriptografadas (usuário/senha ou dados do certificado) são preenchidas nos formulários dos portais judiciais diretamente no navegador do usuário. Esses dados são enviados pelo navegador ao portal judicial — não ao nosso backend.
        </li>
        <li>
          <strong>Resend (serviço de e-mail):</strong> Utilizado exclusivamente para envio de e-mails transacionais (recuperação de senha, notificações de segurança). Nenhum dado além do endereço de e-mail e conteúdo do e-mail é compartilhado.
        </li>
      </ul>

      {/* ======================================================= */}
      <h2 className="text-xl font-semibold mt-8 mb-3">5. Permissões da Extensão e Justificativa Detalhada</h2>
      <p className="mb-4">
        A Extensão solicita apenas as permissões estritamente necessárias para cumprir suas funcionalidades.
        Veja abaixo a justificativa de cada permissão:
      </p>
      <div className="overflow-x-auto my-4">
        <table className="min-w-full bg-white border border-gray-200 text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="py-2 px-4 border-b text-left">Permissão</th>
              <th className="py-2 px-4 border-b text-left">Justificativa</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="py-2 px-4 border-b align-top font-mono">storage</td>
              <td className="py-2 px-4 border-b align-top">Armazenar token JWT de sessão e cache local dos segredos TOTP para funcionamento offline e rápido.</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b align-top font-mono">activeTab</td>
              <td className="py-2 px-4 border-b align-top">Acessar a URL e o DOM da aba ativa para identificar o portal judicial e injetar as credenciais nos campos corretos durante o auto-login.</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b align-top font-mono">tabs</td>
              <td className="py-2 px-4 border-b align-top">Abrir novas abas e monitorar a navegação durante o fluxo de auto-login (ex: aguardar o redirecionamento SSO do PJe/Keycloak).</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b align-top font-mono">scripting</td>
              <td className="py-2 px-4 border-b align-top">Injetar scripts nos portais judiciais para preencher campos de formulário de login automaticamente.</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b align-top font-mono">cookies</td>
              <td className="py-2 px-4 border-b align-top">Remover cookies de sessão expirados dos domínios judiciais (*.jus.br) antes de iniciar o auto-login, evitando conflitos de sessão.</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b align-top font-mono">browsingData</td>
              <td className="py-2 px-4 border-b align-top">Limpar o cache HTTP dos domínios judiciais antes do auto-login para garantir carregamento limpo das páginas de autenticação.</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b align-top font-mono">privacy</td>
              <td className="py-2 px-4 border-b align-top">Desabilitar o gerenciador de senhas nativo do Chrome para impedir que o navegador proponha salvar as credenciais dos portais judiciais preenchidas automaticamente pela extensão.</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b align-top font-mono">debugger</td>
              <td className="py-2 px-4 border-b align-top">Necessário para interceptar e redirecionar o protocolo PJeOffice (cnj.autenticador://) no fluxo de autenticação SSO do PJe — sem necessidade de instalação do aplicativo PJeOffice local.</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b align-top font-mono">declarativeNetRequest / declarativeNetRequestWithHostAccess</td>
              <td className="py-2 px-4 border-b align-top">Modificar cabeçalhos de requisições HTTP nos portais judiciais quando necessário para o fluxo de autenticação SSO (ex: adicionar cabeçalhos CORS para o PJe).</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b align-top font-mono">offscreen</td>
              <td className="py-2 px-4 border-b align-top">Criar um documento offscreen para executar a câmera (webcam) e processar frames de vídeo localmente durante o escaneamento de QR codes.</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b align-top font-mono">clipboardWrite</td>
              <td className="py-2 px-4 border-b align-top">Copiar o código TOTP gerado para a área de transferência quando o usuário clica no botão "Copiar".</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b align-top font-mono">Host permissions (*.jus.br)</td>
              <td className="py-2 px-4 border-b align-top">Necessário para que os content scripts e o processo de auto-login atuem nos portais dos tribunais brasileiros, todos hospedados no domínio *.jus.br.</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ======================================================= */}
      <h2 className="text-xl font-semibold mt-8 mb-3">6. Uso da Câmera</h2>
      <p className="mb-4">
        A Extensão pode solicitar acesso à câmera (webcam) do dispositivo exclusivamente quando o usuário
        clica no botão <strong>"Escanear QR Code via Câmera"</strong> no popup da extensão. O acesso à câmera:
      </p>
      <ul className="list-disc list-inside ml-4 space-y-2 mb-4">
        <li>É iniciado somente mediante ação explícita do usuário.</li>
        <li>É utilizado exclusivamente para detectar e decodificar QR codes de segredos TOTP (no formato <code>otpauth://totp/</code>).</li>
        <li>Todo o processamento de imagem é realizado localmente no dispositivo usando a biblioteca jsQR. Nenhum frame ou imagem é transmitido a servidores externos.</li>
        <li>A câmera é desligada imediatamente após a detecção bem-sucedida do QR code ou ao fechar o popup.</li>
      </ul>

      {/* ======================================================= */}
      <h2 className="text-xl font-semibold mt-8 mb-3">7. Retenção de Dados</h2>
      <ul className="list-disc list-inside ml-4 space-y-2 mb-4">
        <li><strong>Dados de conta (e-mail, senha hash):</strong> Retidos enquanto a conta estiver ativa. Removidos mediante solicitação de exclusão de conta.</li>
        <li><strong>Segredos TOTP, certificados e credenciais:</strong> Retidos até o usuário excluir manualmente cada item através da interface da extensão ou do painel web.</li>
        <li><strong>Logs de auditoria:</strong> Retidos por até 90 dias para fins de segurança e rastreabilidade.</li>
        <li><strong>Token de sessão (local):</strong> Expirado automaticamente após 15 minutos de inatividade e removido ao logout.</li>
        <li><strong>Dados processados localmente (câmera, captura de tela, cookies, cache):</strong> Não são armazenados. Processados em memória e descartados imediatamente.</li>
      </ul>

      {/* ======================================================= */}
      <h2 className="text-xl font-semibold mt-8 mb-3">8. Direitos do Usuário</h2>
      <p className="mb-4">Em conformidade com a Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018), o usuário tem direito a:</p>
      <ul className="list-disc list-inside ml-4 space-y-2 mb-4">
        <li><strong>Acesso:</strong> Solicitar uma cópia de todos os dados pessoais armazenados.</li>
        <li><strong>Retificação:</strong> Corrigir dados incorretos ou desatualizados.</li>
        <li><strong>Exclusão:</strong> Solicitar a exclusão de dados pessoais, incluindo segredos TOTP, certificados e credenciais. A exclusão da conta remove todos os dados associados.</li>
        <li><strong>Portabilidade:</strong> Solicitar os dados em formato estruturado.</li>
        <li><strong>Revogação de consentimento:</strong> Excluir a conta e todos os dados a qualquer momento.</li>
      </ul>
      <p className="mb-4">Para exercer seus direitos, entre em contato pelo e-mail indicado na seção de Contato abaixo.</p>

      {/* ======================================================= */}
      <h2 className="text-xl font-semibold mt-8 mb-3">9. Dados de Menores</h2>
      <p className="mb-4">
        O JurisKey é destinado exclusivamente a profissionais do Direito (advogados, operadores jurídicos e magistrados).
        Não coletamos intencionalmente dados de menores de 18 anos.
      </p>

      {/* ======================================================= */}
      <h2 className="text-xl font-semibold mt-8 mb-3">10. Alterações nesta Política</h2>
      <p className="mb-4">
        Esta Política pode ser atualizada para refletir mudanças na extensão, na legislação aplicável ou nas práticas
        de privacidade. A data de "Última Atualização" no topo deste documento será revisada a cada alteração.
        Alterações significativas serão comunicadas via e-mail ou através de um aviso no popup da extensão.
      </p>

      {/* ======================================================= */}
      <h2 className="text-xl font-semibold mt-8 mb-3">11. Contato</h2>
      <p className="mb-4">
        Para dúvidas sobre esta Política de Privacidade, solicitações de direitos do titular ou relatos de incidentes
        de segurança, entre em contato pelo e-mail de suporte disponível na página da extensão na Chrome Web Store
        ou pelo canal de suporte da plataforma JurisKey.
      </p>
    </div>
  );
};

export default PrivacyPolicy;
